import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { assessmentsAPI, jobsAPI } from '../utils/api';
import './Dashboard.css';
import './CandidateDashboard.css';

function CandidateDashboard({ user }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [guidelinesExpanded, setGuidelinesExpanded] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [assessmentsRes, applicationsRes] = await Promise.allSettled([
        assessmentsAPI.getAll(),
        jobsAPI.getMyApplications()
      ]);

      if (assessmentsRes.status === 'fulfilled') {
        setAssessments(assessmentsRes.value.data.assessments || []);
      }
      if (applicationsRes.status === 'fulfilled') {
        setApplications(applicationsRes.value.data.applications || []);
      }
    } catch (error) {
      console.error('[CandidateDashboard] Failed to load data:', error);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartAssessment = (assessment) => {
    const assessmentId = assessment._id || assessment.id;

    if (!assessmentId) {
      setError('Unable to start assessment - invalid assessment ID');
      return;
    }

    navigate(`/assessment/${assessmentId}`);
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-state">
          <div className="cyber-spinner"></div>
          <p>Loading your assessments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard candidate-dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>Security Assessment Labs</h1>
          <div className="user-info">
            <span>{user.name || user.email}</span>
            <button onClick={logout} className="btn btn-secondary">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        {/* Success Message */}
        {successMessage && (
          <div className="notification success-notification">
            <span className="notification-message">{successMessage}</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="notification error-notification">
            <span className="notification-message">{error}</span>
          </div>
        )}

        {/* Welcome Section */}
        <section className="welcome-section">
          <h2>Welcome, {user.name || user.email}!</h2>
          <p>Your assigned security assessments.</p>
        </section>

        {/* Assessment Guidelines - Clean Professional UI with Collapse */}
        <section className="guidelines-section">
          <div className="guidelines-container">
            <button
              className="guidelines-toggle"
              onClick={() => setGuidelinesExpanded(!guidelinesExpanded)}
              aria-expanded={guidelinesExpanded}
            >
              <div className="guidelines-toggle-header">
                <div className="guidelines-title">
                  <span className="guidelines-badge">Info</span>
                  <h3>Before You Start</h3>
                </div>
                <svg
                  className={`toggle-icon ${guidelinesExpanded ? 'expanded' : ''}`}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              {!guidelinesExpanded && (
                <p className="guidelines-preview">
                  Complete assessments assigned by your recruiter • Follow time limits • Submit work on time
                </p>
              )}
            </button>

            {guidelinesExpanded && (
              <div className="guidelines-content">
                <ul className="guidelines-list">
                  <li>Complete only the assessments assigned to you by your recruiter</li>
                  <li>Pay attention to scheduled times and deadlines</li>
                  <li>Read each challenge scenario carefully before starting</li>
                  <li>Some challenges provide live Docker environments for hands-on testing</li>
                  <li>Use hints strategically if you get stuck</li>
                  <li>Submit flags in the exact format specified</li>
                </ul>
              </div>
            )}
          </div>
        </section>

        {/* Assigned Assessments */}
        <section className="assessments-section">
          <h2>Your Assessments</h2>
          {assessments.length === 0 ? (
            <div className="empty-state">
              <h3>No Assessments Assigned</h3>
              <p>You currently have no assessments assigned to you.</p>
              <p>Your recruiter will assign assessments when they are ready.</p>
            </div>
          ) : (
            <div className="assessments-grid">
              {assessments.map((assessment) => (
                <div key={assessment._id} className="assessment-card">
                  <div className="card-header">
                    <h3>{assessment.name}</h3>
                    <span className={`status-badge status-${assessment.status === 'expired' || assessment.labExpired ? 'expired' : assessment.status}`}>
                      {assessment.status === 'expired' ? 'EXPIRED' : assessment.labExpired ? 'LAB EXPIRED' : (assessment.status?.toUpperCase() || 'PENDING')}
                    </span>
                  </div>
                  <div className="card-body">
                    <div className="info-row">
                      <label>Position:</label>
                      <span>{assessment.position}</span>
                    </div>
                    <div className="info-row">
                      <label>Mode:</label>
                      <span className="mode-badge">{assessment.mode?.toUpperCase() || 'ASYNC'}</span>
                    </div>
                    {assessment.scheduling?.availableUntil && (
                      <div className="info-row">
                        <label>Deadline:</label>
                        <span>{new Date(assessment.scheduling.availableUntil).toLocaleString()}</span>
                      </div>
                    )}

                    {/* Challenge Labs List */}
                    {assessment.challengeSessions && assessment.challengeSessions.length > 0 && (
                      <div style={{ marginTop: '1rem' }}>
                        <label style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem', display: 'block' }}>
                          Challenge Labs ({assessment.challengeSessions.length})
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {assessment.challengeSessions.map((cs, idx) => (
                            <div key={cs.sessionId || idx} style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.6rem 0.8rem',
                              background: cs.status === 'completed' ? 'var(--success-light, #d4edda)' : 'var(--bg-secondary, #f8f9fa)',
                              borderRadius: '6px',
                              border: '1px solid var(--border-light, #e9ecef)',
                              fontSize: '0.85rem'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{
                                  width: '8px', height: '8px', borderRadius: '50%',
                                  background: cs.status === 'completed' ? '#28a745' : cs.status === 'active' ? '#007bff' : '#6c757d',
                                  flexShrink: 0
                                }} />
                                <span style={{ fontWeight: 500 }}>{cs.challenge?.title || `Challenge ${idx + 1}`}</span>
                                {cs.challenge?.difficulty && (
                                  <span style={{
                                    fontSize: '0.7rem', padding: '1px 6px', borderRadius: '3px',
                                    background: cs.challenge.difficulty === 'junior' ? '#e3f2fd' : cs.challenge.difficulty === 'senior' ? '#fff3e0' : '#fce4ec',
                                    color: cs.challenge.difficulty === 'junior' ? '#1565c0' : cs.challenge.difficulty === 'senior' ? '#e65100' : '#c62828'
                                  }}>{cs.challenge.difficulty}</span>
                                )}
                              </div>
                              <span style={{
                                fontSize: '0.75rem', fontWeight: 500,
                                color: cs.status === 'completed' ? '#28a745' : cs.status === 'active' ? '#007bff' : '#6c757d'
                              }}>
                                {cs.status === 'completed' ? 'Done' : cs.status === 'active' ? 'Active' : cs.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {(!assessment.challengeSessions || assessment.challengeSessions.length === 0) && assessment.challenges?.count > 0 && (
                      <div className="info-row" style={{ marginTop: '0.5rem' }}>
                        <label>Challenges:</label>
                        <span>{assessment.challenges.count} labs ({assessment.challenges.difficulty})</span>
                      </div>
                    )}
                  </div>
                  <div className="card-actions">
                    {(assessment.status === 'expired' || assessment.labExpired) && assessment.labExpiredAt && (
                      <div className="resume-indicator" style={{
                        background: 'var(--gray-100)',
                        color: 'var(--gray-700)',
                        border: '1px solid var(--gray-300)'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="8" x2="12" y2="12"/>
                          <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                        <span>Lab expired on {new Date(assessment.labExpiredAt).toLocaleString()}</span>
                      </div>
                    )}
                    {!assessment.labExpired && assessment.status === 'in_progress' && (
                      <div className="resume-indicator">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        <span>You can resume where you left off</span>
                      </div>
                    )}
                    {assessment.status === 'expired' || assessment.labExpired ? (
                      <button
                        className="btn btn-primary"
                        onClick={() => navigate(`/assessment/${assessment._id}/results`)}
                      >
                        View Results
                      </button>
                    ) : (
                      <button
                        className={`btn ${assessment.status === 'in_progress' && !assessment.labExpired ? 'btn-success' : 'btn-primary'}`}
                        onClick={() => {
                          if (assessment.status === 'completed') {
                            navigate(`/assessment/${assessment._id}/results`);
                          } else {
                            handleStartAssessment(assessment);
                          }
                        }}
                        disabled={assessment.status === 'cancelled'}
                      >
                        {assessment.status === 'completed'
                          ? 'View Results'
                          : assessment.status === 'in_progress'
                            ? 'Resume Assessment'
                            : assessment.status === 'cancelled'
                              ? 'Cancelled'
                              : 'Start Assessment'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Your Applications Section */}
        <section style={{ marginTop: '2rem' }}>
          <h2 style={{ marginBottom: '1rem' }}>Your Applications</h2>
          {applications.length === 0 ? (
            <p style={{ color: 'var(--text-tertiary)', padding: '1rem' }}>No applications found.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {applications.map((app) => {
                const statusColors = {
                  new: { bg: '#e3f2fd', color: '#1565c0', label: 'Applied' },
                  screening: { bg: '#fff3e0', color: '#e65100', label: 'Screening' },
                  shortlisted: { bg: '#e8f5e9', color: '#2e7d32', label: 'Shortlisted' },
                  assessment_sent: { bg: '#f3e5f5', color: '#7b1fa2', label: 'Assessment Sent' },
                  interview_scheduled: { bg: '#e0f2f1', color: '#00695c', label: 'Interview Scheduled' },
                  offered: { bg: '#e8f5e9', color: '#1b5e20', label: 'Offer Made' },
                  hired: { bg: '#c8e6c9', color: '#1b5e20', label: 'Hired' },
                  rejected: { bg: '#ffebee', color: '#c62828', label: 'Not Selected' },
                  withdrawn: { bg: '#f5f5f5', color: '#757575', label: 'Withdrawn' },
                  questionnaire_completed: { bg: '#e8eaf6', color: '#283593', label: 'Questionnaire Done' },
                };
                const s = statusColors[app.status] || { bg: '#f5f5f5', color: '#616161', label: app.status };

                return (
                  <div key={app.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1rem 1.25rem',
                    background: 'var(--bg-primary, #fff)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-light, #e9ecef)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                        {app.jobTitle}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', marginTop: '0.25rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                        {app.location && <span>{app.location}</span>}
                        {app.department && <span>{app.department}</span>}
                        {app.employmentType && <span>{app.employmentType}</span>}
                        <span>Applied {new Date(app.appliedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <span style={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '4px 10px',
                      borderRadius: '12px',
                      background: s.bg,
                      color: s.color,
                      whiteSpace: 'nowrap'
                    }}>{s.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default CandidateDashboard;
